/**
 * Is the live text ALREADY the settled block — even when the pane rendered it
 * differently from the transcript's raw markdown?
 *
 * LiveBlock's own-copy check was exact containment on normalized text, and that is
 * the right answer for the common case: the pane shows the same characters the
 * transcript stores, minus punctuation and whitespace, so norm(live) is either the
 * whole settled block or a suffix of it. It has no answer for a render that is not
 * a substring — a table the pane reflowed and cell-truncated, a long line the
 * terminal broke inside a word — and the machine already solved exactly that on
 * its side with shingle coverage (edge paneLive.ts transcriptCoverage): most
 * 12-grams of a damaged render still appear in the raw text.
 *
 * This is that gate, ported, applied to ONE block: the newest thing the agent said
 * that the transcript holds. The cost of a false positive is the live block hiding
 * for the second it takes the transcript to catch up; the cost of a false negative
 * is the owner's report — the last block drawn twice, the second copy with a caret
 * blinking on it.
 */
const norm = (t: string): string => t.replace(/[^\p{L}\p{N}]+/gu, '')

const SHINGLE = 12
/** Past this share of 12-grams, the live text is assembled from the block, not new. */
const COVERED = 0.55
/** Below this many normalized characters there are too few shingles to testify;
 *  containment (which has its own floor) decides instead. */
const COVER_FLOOR = 24

export function coverage(candidate: string, settled: string): number {
  const mine = norm(candidate)
  const have = norm(settled)
  if (mine.length < SHINGLE || have.length < SHINGLE) return 0
  let hit = 0
  let total = 0
  for (let i = 0; i + SHINGLE <= mine.length; i += 4) {
    total++
    if (have.includes(mine.slice(i, i + SHINGLE))) hit++
  }
  return total ? hit / total : 0
}

/**
 * Is `recorded` — the transcript's newest user block — the message `sent` stood in for?
 * SentEcho's handover (owner, 2026-09-29: the message block repeated — his 「推 prod推」
 * stayed on screen under its own record).
 *
 * It used `coverage` alone, and coverage is 0 for anything under twelve normalized
 * characters: a short message could NEVER be handed over, so every 「繼續」 or 「推 prod」
 * sat at the bottom of the conversation as a second copy of itself. Short messages need
 * the exact test; the CLI adding to what was typed needs containment (the same
 * four-character floor as below); only long messages the CLI rewrapped need coverage —
 * held at `longAt` (0.8), stricter than the live block's, so a new message that merely
 * resembles the previous one is not taken for it.
 */
export function sameMessage(sent: string, recorded: string | null, longAt: number): boolean {
  if (!recorded) return false
  const mine = norm(sent)
  if (!mine.length) return false
  const have = norm(recorded)
  if (mine === have) return true
  if (mine.length >= 4 && have.includes(mine)) return true
  return coverage(sent, recorded) >= longAt
}

/**
 * The whole rule, in one place. Exact equality has no floor (the same message is
 * the same message however short); containment keeps the FOUR-character floor the
 * edge and the 2026-09-02 fix agreed on; coverage stands above the shingle floor.
 */
export function explainedBySettled(live: string, settled: string | null): boolean {
  if (!settled) return false
  const mine = norm(live)
  if (!mine.length) return false
  const have = norm(settled)
  if (mine === have) return true
  if (mine.length >= 4 && have.includes(mine)) return true
  return mine.length >= COVER_FLOOR && coverage(live, settled) > COVERED
}
